/**
 * Black Box — entry point. Exposes window.BlackBox
 * (window.NojuBlackBox kept as a back-compat alias for pages using the old name).
 *
 * Usage on a page — ONE line, records by default:
 *   <script src=".../blackbox.js" data-bb-key="bb_…"></script>
 *
 * Boot is a single router (boot): ?replay=<id> → replay mode (never records);
 * otherwise → record mode. Both consume the same resolved config: defaults <
 * preset < tag attrs, with the dashboard site config (fetched once by key)
 * merged on top as the authority for funnel + activation.
 */
import { DEFAULT_CONFIG } from './schema.js';
import { PRESETS, matchPreset } from './presets.js';
import { createTransport } from './transport.js';
import { createTracer } from './tracer.js';
import { runReplay } from './replay.js';
import { installPicker } from './picker.js';
import {
  parseUA, connectionType, guessMarket, language, makeSessionId, hasFlag, siteName,
} from './util.js';

let instance = null;

// The <script> that loaded us. document.currentScript works for a normal
// parser-inserted tag, but is null when we're injected programmatically — a
// SPA useEffect, or a GTM Custom HTML tag. In that case find our own tag by its
// key (only our tag carries data-bb-key), falling back to the bundle filename.
function findSelf() {
  if (typeof document === 'undefined') return null;
  if (document.currentScript) return document.currentScript;
  try {
    return document.querySelector('script[data-bb-key]')
      || document.querySelector('script[src*="blackboks.js"], script[src*="blackbox.js"]')
      || null;
  } catch (_) { return null; }
}
const SCRIPT_EL = findSelf();
function scriptAttr(name) { try { return SCRIPT_EL && SCRIPT_EL.getAttribute(name); } catch (_) { return null; } }
function urlParam(name) { try { return new URLSearchParams(location.search).get(name); } catch (_) { return null; } }
// Origin the script was served from — lets a bare tag (just src + key) reach the
// collector + config API without explicit data-bb-endpoint / data-bb-api.
function scriptOrigin() { try { return SCRIPT_EL && SCRIPT_EL.src ? new URL(SCRIPT_EL.src).origin : null; } catch (_) { return null; } }

/** Page-declared config: preset (page-specific selectors) + data-* tag attributes. */
function pageConfig() {
  const key = scriptAttr('data-bb-preset') || urlParam('bbpreset') || matchPreset(location);
  const preset = (key && PRESETS[key]) || {};
  const overrides = {};
  const origin = scriptOrigin();
  // Build-time baked prod API host (empty in dev/demo builds). Guarded so the
  // reference is safe even if `define` didn't replace it.
  const bakedEndpoint = (typeof __BB_ENDPOINT__ !== 'undefined' && __BB_ENDPOINT__) || null;
  const bakedApiBase = (typeof __BB_API_BASE__ !== 'undefined' && __BB_API_BASE__) || null;
  // Precedence: explicit data-* > baked prod host > the script's own origin (bare
  // same-origin tag) > relative. The baked value is what makes the CDN-hosted
  // bundle reach pages-apis instead of its own (cdn) origin.
  const endpoint = scriptAttr('data-bb-endpoint') || bakedEndpoint || (origin ? origin + '/ingest' : null); if (endpoint) overrides.endpoint = endpoint;
  const apiBase = scriptAttr('data-bb-api') || bakedApiBase || origin; if (apiBase) overrides.apiBase = apiBase;
  const siteKey = scriptAttr('data-bb-key'); if (siteKey) overrides.siteKey = siteKey;
  // Explicit page version (deploy hash/date) for the Deployments benchmark; when
  // absent, the tracer falls back to a DOM fingerprint at snapshot time.
  const pageVersion = scriptAttr('data-bb-version'); if (pageVersion) overrides.pageVersion = pageVersion;
  return { preset, overrides };
}

// Ad-source / campaign params off the landing URL (utm_*, gclid, fbclid, msclkid).
// Campaign identifiers only — no PII. Server normalizes these into a `source` label.
function parseSource() {
  try {
    const q = new URLSearchParams(location.search);
    const s = {};
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'msclkid']) {
      const v = q.get(k);
      if (v) s[k] = v.slice(0, 200); // guard against absurdly long values
    }
    return Object.keys(s).length ? s : null;
  } catch (_) { return null; }
}

// The tracer bundle version (baked at build time; 'dev' in unversioned builds).
const BB_VERSION = (typeof __BB_VERSION__ !== 'undefined' && __BB_VERSION__) || 'dev';

function buildContext(config) {
  const ua = parseUA();
  return {
    bb_version: BB_VERSION, // tracer library version (distinct from page_version)
    device: ua.device,
    browser: ua.browser,
    os: ua.os,
    viewport: [window.innerWidth, window.innerHeight],
    screen: [window.screen ? window.screen.width : null, window.screen ? window.screen.height : null],
    market: config.market || guessMarket(),
    language: language(),
    site: config.site || siteName(),
    origin: location.origin,
    path: location.pathname,
    referrer: document.referrer || null,
    connection: connectionType(),
    source: parseSource(),
    // Explicit deploy version if the tag declares one; else the tracer fills this
    // with a DOM fingerprint once the snapshot is captured (see tracer.js).
    page_version: config.pageVersion || null,
  };
}

/**
 * Fetch the dashboard-managed site config by site-key. Fully fail-safe AND
 * fail-open: on any error or after a 2s timeout it resolves null, and the tracer
 * proceeds with defaults (record everything) rather than losing sessions.
 */
function fetchSiteConfig(cfg) {
  try {
    if (!cfg.siteKey || !cfg.apiBase || typeof fetch === 'undefined') return Promise.resolve(null);
    const url = String(cfg.apiBase).replace(/\/$/, '') + '/config?key=' + encodeURIComponent(cfg.siteKey);
    const req = fetch(url, { credentials: 'omit' })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .catch(function () { return null; });
    const timeout = new Promise(function (resolve) { setTimeout(resolve, 2000, null); });
    return Promise.race([req, timeout]);
  } catch (_) { return Promise.resolve(null); }
}

/** Merge a fetched site config into `config`: funnel (steps/fields/errors/conversion),
 *  site label, AND activation — the dashboard can sample traffic or disable recording. */
function applyRemote(config, remote) {
  if (!remote || typeof remote !== 'object') return;
  for (const k of ['steps', 'fields', 'errors', 'conversion']) {
    if (remote[k]) config[k] = Object.assign({}, config[k], remote[k]);
  }
  if (remote.site && !config.site) config.site = remote.site;
  if (typeof remote.sample === 'number') config.sample = remote.sample;
  if (remote.enabled === false) config.enabled = false;
}

/**
 * The ONE config resolution, shared by both modes (cached). Layers:
 * defaults < preset < tag attrs, then the dashboard config merged on top when the
 * tag carries a site key. Key-less pages resolve synchronously with no fetch.
 */
let configPromise = null;
function resolveConfig() {
  if (configPromise) return configPromise;
  const { preset, overrides } = pageConfig();
  const config = Object.assign({}, DEFAULT_CONFIG, preset, overrides);
  // shallow-merge nested objects so partial overrides don't wipe defaults
  for (const k of ['steps', 'fields', 'errors', 'conversion']) {
    config[k] = Object.assign({}, DEFAULT_CONFIG[k], preset[k] || {});
  }
  config.debug = config.debug || hasFlag('bbdebug', 'true');
  configPromise = (config.siteKey && config.apiBase)
    ? fetchSiteConfig(config).then(function (remote) { applyRemote(config, remote); return config; })
    : Promise.resolve(config);
  return configPromise;
}

/** Record-or-not, decided AFTER the dashboard config is in (kill-switch + sampling). */
function shouldRecord(config) {
  // Embedded preview (e.g. the dashboard heatmap iframe) — render live, never record the viewer.
  if (hasFlag('bbpreview', '1')) return false;
  // Dashboard kill-switch (site config `enabled: false`).
  if (config.enabled === false) return false;
  // Recording is on by default; `sample` (0..1) throttles prod traffic.
  return Math.random() < (config.sample == null ? 1 : config.sample);
}

// ---- mode: record (the default) ---------------------------------------------
function recordMode() {
  resolveConfig().then(function (config) {
    if (!shouldRecord(config)) return;
    try {
      const session = { id: makeSessionId(), context: buildContext(config) };
      const transport = createTransport(config, session);
      const tracer = createTracer(config, session, transport);
      instance = {
        sessionId: session.id,
        markConverted: tracer.markConverted,
        track: tracer.track,
        getStep: tracer.getStep,
        end: tracer.end,
      };
      tracer.start();
      while (pending.length) { try { pending.shift()(instance); } catch (_) {} }
    } catch (_) {
      // fail silent — never break the host page
    }
  });
}

// ---- mode: replay (?replay=<id>) — re-enacts a session, records nothing -----
function replayMode() {
  resolveConfig().then(function (config) {
    try { runReplay(config); } catch (_) {}
  });
}

function boot() {
  if (urlParam('replay')) replayMode();
  else recordMode();
}

// Calls made before recording starts (the site config fetch is async) are
// queued and replayed once the tracer is live.
const pending = [];
function whenLive(fn) { if (instance) { try { fn(instance); } catch (_) {} } else if (pending.length < 50) pending.push(fn); }

const BlackBox = {
  version: BB_VERSION, // introspection: window.BlackBox.version
  get sessionId() { return instance ? instance.sessionId : null; },
  markConverted(reason) { whenLive((i) => i.markConverted(reason)); },
  // Any site, no config: BlackBox.track('signup_started'), BlackBox.convert('purchase').
  track(name) { whenLive((i) => i.track(name)); },
  convert(name) { whenLive((i) => { if (name) i.track(name); i.markConverted(name ? 'goal:' + name : 'api'); }); },
  // Removed API — config comes from the dashboard. Inert stub so a stale cached
  // page calling init() can't throw on a live checkout.
  init() { return instance; },
};

if (typeof window !== 'undefined' && !window.__bbLoaded) {
  window.__bbLoaded = true;   // single-instance guard: never double-inject/observe
  window.BlackBox = BlackBox;
  window.NojuBlackBox = BlackBox; // deprecated alias — pages still calling the old name
  // Flow Setup / Heatmap's click-to-pick + highlight — independent of record/replay
  // mode. Shares the same memoized config resolution (no extra fetch) so a
  // highlight request can navigate to the right step first (config.steps order).
  installPicker(resolveConfig());
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}

export default BlackBox;
